import { FormContainer, LabeledInput, Worksheet } from "uikit"
import React from "react"
import { Dropdown, Grid } from "semantic-ui-react"
import { useStore } from "store"
import { AddPersonModal } from "../../../people/AddPersonModal"
import ConnectedAccountBar from "../../ConnectedAccountBar"
import { Footer } from "./Footer"

const TermLifeForm = ({
  data: {
    fields: {
      person_id,
      death_benefit,
      premium_amount,
      premium_frequency,
      origination_year,
      origination_month,
      term_years
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  ownerOptions,
  isNew,
  onDelete,
  addNewPerson,
  frequencyOptions,
  originationYearOptions,
  termYearOptions,
  originationMonthOptions,
  account
}) => {
  return (
    <Grid>
      {account.connection_id ? (
        <Grid.Row style={{ borderBottom: "1px solid #ccc" }}>
          <Grid.Column>
            <ConnectedAccountBar {...account} />
          </Grid.Column>
        </Grid.Row>
      ) : null}
      <Grid.Row>
        <Grid.Column>
          <Worksheet error={error} style={{ borderTop: "1px solid #ccc" }}>
            <Worksheet.Body>
              <Worksheet.Row label="Who is insured by this policy?" paddedField>
                <Worksheet.Field error={person_id.error}>
                  <Dropdown
                    {...person_id}
                    placeholder="Insured Person"
                    options={ownerOptions}
                    selection
                    data-testid="accountOwner"
                  />
                  {person_id.value === "__ADD__" && (
                    <AddPersonModal
                      submit={personValues =>
                        addNewPerson(personValues).then(({ personId }) => {
                          person_id.onChange(null, { value: personId })
                        })
                      }
                      onCancel={() => person_id.onChange(null, { value: null })}
                    />
                  )}
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row label="What is the coverage amount?" paddedField>
                <Worksheet.Field error={death_benefit.error}>
                  <LabeledInput
                    {...death_benefit}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    data-testid="death_benefit"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="What is the premium paid for this policy?"
                paddedField
              >
                <Worksheet.Field error={premium_amount.error}>
                  <LabeledInput
                    {...premium_amount}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                    data-testid="premium_amount"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row label="How often is the premium paid?" paddedField>
                <Worksheet.Field error={premium_frequency.error}>
                  <Dropdown
                    {...premium_frequency}
                    selection
                    placeholder="Premium Frequency"
                    options={frequencyOptions}
                    search
                    data-testid="premium_fequency"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="In what year did the policy go into effect?"
                paddedField
              >
                <Worksheet.Field error={origination_year.error}>
                  <Dropdown
                    {...origination_year}
                    selection
                    placeholder="Policy effective from (Year)"
                    options={originationYearOptions}
                    search
                    data-testid="origination_year"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="In what month did the policy go into effect?"
                paddedField
              >
                <Worksheet.Field error={origination_month.error}>
                  <Dropdown
                    {...origination_month}
                    selection
                    placeholder="Policy effective from (Month)"
                    options={originationMonthOptions}
                    search
                    data-testid="origination_month"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
              <Worksheet.Row
                label="How many years of coverage does this policy provide?"
                description="Keep in mind that if you don't pay the premium, the policy will terminate before the end of the term."
                paddedField
              >
                <Worksheet.Field error={term_years.error}>
                  <Dropdown
                    {...term_years}
                    selection
                    placeholder="Policy Duration"
                    options={termYearOptions}
                    search
                    data-testid="term_years"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            </Worksheet.Body>
            <Footer
              isNew={isNew}
              submitLabel={submitLabel}
              onSubmit={onSubmit}
              onDelete={onDelete}
              isModified={isModified}
              deleteLabel={
                account.connection_id ? "Ignore Account" : "Delete Account"
              }
              hasConnectionId={account.connection_id ? true : false}
              error={error}
            />
          </Worksheet>{" "}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const TermLifeFormContainer = FormContainer(
  {
    person_id: {
      type: "string"
    },
    death_benefit: {
      type: "unit"
    },
    premium_amount: {
      type: "unit"
    },
    premium_frequency: {
      type: "string"
    },
    origination_year: {
      type: "unit",
      useGrouping: false
    },
    origination_month: {
      type: "unit"
    },
    term_years: {
      type: "unit",
      minValue: 1,
      maxValue: 30
    }
  },
  (
    { fields: { origination_month, origination_year, term_years } },
    ...form
  ) => {
    const currentMonth = new Date().getMonth()
    const currentYear = new Date().getFullYear()
    const originationYear = Number(origination_year.value)
    const originationMonth = Number(origination_month.value)
    const termYears = Number(term_years.value)
    if (
      originationYear + termYears < currentYear ||
      (originationYear + termYears === currentYear &&
        originationMonth < currentMonth)
    ) {
      return {
        fields: {
          term_years: `Please check start year and term as this policy looks expired.`
        }
      }
    } else {
      return {
        fields: {
          term_years: ``
        }
      }
    }
  }
)(TermLifeForm)

const TermLife = ({ household, account, ...formProps }) => {
  const isNew = account == null
  const [people, { addPerson }] = useStore("householdPeople", household.id)
  const ownerOptions = Object.values(people)
    .filter(person => person.role === "spouse" || person.role === "client")
    .map(({ id: value, first_name: text }) => ({ value, text }))
  const defaultOwnerId = ownerOptions.length === 1 ? ownerOptions[0].value : ""
  const values = isNew ? { person_id: defaultOwnerId } : account
  const canAddOwner = false
  const currentYear = new Date().getFullYear()
  const originationYearOptions = Array(30)
    .fill(0)
    .map((_, index) => ({
      text: currentYear - index,
      value: (currentYear - index).toString()
    }))
  const termYearOptions = ["10", "15", "20", "30"].map(term => ({
    text: term,
    value: term
  }))
  const frequencyOptions = [
    { text: "Monthly", value: "monthly" },
    { text: "Quarterly", value: "quarterly" },
    { text: "Semiannually", value: "semiannually" },
    { text: "Annually", value: "annually" }
  ]
  const monthArray = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]
  const originationMonthOptions = Array(12)
    .fill(0)
    .map((_, index) => ({
      text: monthArray[index],
      value: (index + 1).toString()
    }))
  if (canAddOwner) {
    ownerOptions.push({ text: "Add person...", value: "__ADD__" })
  }
  const addNewPerson = values =>
    addPerson({ household_id: household.id, ...values })
  return (
    <TermLifeFormContainer
      {...formProps}
      ownerOptions={ownerOptions}
      values={values}
      addNewPerson={addNewPerson}
      account={values}
      termYearOptions={termYearOptions}
      originationYearOptions={originationYearOptions}
      frequencyOptions={frequencyOptions}
      originationMonthOptions={originationMonthOptions}
    />
  )
}

export default TermLife
